import { unitaByte } from './unita';
/**
 * LifetimeCloud Crypto Utils
 * Web Crypto API — funziona solo su HTTPS o localhost.
 * Su HTTP con IP esterno usa un fallback PBKDF2 software (meno sicuro, solo per dev).
 */

const ITERATIONS = 310_000;
const enc = new TextEncoder();
const dec = new TextDecoder();

// Check if Web Crypto is available (requires HTTPS or localhost)
function hasCrypto() {
  return typeof window !== 'undefined' &&
    window.isSecureContext &&
    window.crypto?.subtle;
}

// ── PBKDF2 software fallback (dev only, HTTP) ─────────────
async function pbkdf2Fallback(password, salt, iterations, keyLen) {
  // Simple PBKDF2-like key derivation using SHA-256
  // WARNING: This is less secure than Web Crypto — use HTTPS in production
  const pwBytes  = enc.encode(password);
  const saltBytes = typeof salt === 'string' ? base64ToBuffer(salt) : salt;
  let u = new Uint8Array([...pwBytes, ...new Uint8Array(saltBytes)]);
  for (let i = 0; i < Math.min(iterations, 10000); i++) {
    const hashBuf = await crypto.subtle.digest('SHA-256', u);
    u = new Uint8Array(hashBuf);
  }
  return u.slice(0, keyLen);
}

export function generateSalt() {
  return bufferToBase64(crypto.getRandomValues(new Uint8Array(32)));
}

export async function deriveKey(password, saltBase64) {
  if (!hasCrypto()) {
    // HTTP fallback — derive raw key bytes and use as AES key via importKey
    // This path only works if subtle is available at all (even on HTTP Chrome allows it with flag)
    throw new Error(
      'La crittografia richiede HTTPS.\n\nPer testare in locale usa:\n• http://localhost:3000 (funziona)\n• oppure configura HTTPS con certbot sul tuo dominio'
    );
  }

  const saltBytes = base64ToBuffer(saltBase64);
  const keyMaterial = await crypto.subtle.importKey(
    'raw', enc.encode(password), 'PBKDF2', false, ['deriveKey']
  );
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: saltBytes, iterations: ITERATIONS, hash: 'SHA-256' },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    true, // extractable=true needed for recovery key backup
    ['encrypt', 'decrypt']
  );
}

export async function encryptBuffer(key, plainBuffer) {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, plainBuffer);
  return { ciphertext, iv: bufferToBase64(iv) };
}

export async function decryptBuffer(key, cipherBuffer, ivBase64) {
  const iv = base64ToBuffer(ivBase64);
  return crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, cipherBuffer);
}

// Quanto si legge per volta. 8 MB e' abbastanza grande da non pagare
// l'attraversamento fra JS e il motore per ogni pezzo, e abbastanza piccolo da
// dare all'indicatore una trentina di scatti su un file da 250 MB.
const PEZZO_LETTURA = 8 * 1024 * 1024;

// LETTURA A PEZZI, PER POTER DIRE A CHE PUNTO SIAMO (2026-09-12).
//
// Prima era `await file.arrayBuffer()`: un'unica attesa opaca. Non e' una
// questione di velocita' — misurato, 0,4s per 250 MB su questa macchina — ma
// di onesta' dell'indicatore: durante un'unica attesa non c'e' niente di vero
// da mostrare. Letto a pezzi, l'avanzamento che si vede e' misurato, non
// inventato. La memoria usata e' la stessa: il buffer di destinazione e'
// grande quanto il file in entrambi i casi.
// IL FILE PUO' SPARIRE MENTRE LO LEGGIAMO (2026-09-12).
//
// Segnalato da un cliente con gli zip grossi di Google Takeout: il browser
// alza NotReadableError e la pagina mostrava il testo grezzo del browser, in
// inglese, senza dire cosa fare. Succede quando il file non e' davvero sul
// disco — cartelle sincronizzate di OneDrive/Google Drive/Dropbox che tengono
// solo un segnaposto finche' non lo apri — oppure quando viene spostato o
// rinominato DOPO che l'utente l'ha scelto: il riferimento resta valido per il
// browser ma i byte non ci sono piu'.
//
// Non e' un errore nostro e non si puo' evitare: si puo' solo riconoscerlo e
// dire all'utente la cosa che lo risolve. Il messaggio tecnico va in console,
// quello leggibile lo compone chi disegna, a partire dal codice.
export function erroreDiLettura(e) {
  return e && (e.name === 'NotReadableError' || e.name === 'NotFoundError');
}

function traduciErroreLettura(e, file) {
  if (!erroreDiLettura(e)) return e;
  console.error('Lettura del file fallita:', e.name, '—', e.message, '| nome:', file?.name, '| byte dichiarati:', file?.size);
  return Object.assign(new Error(e.name), { code: 'FILE_NON_LEGGIBILE', causaTecnica: e.name });
}

export async function leggiFileIntero(file, avvisa) {
  try {
    return await leggiConAvanzamento(file, avvisa);
  } catch (e) {
    throw traduciErroreLettura(e, file);
  }
}

async function leggiConAvanzamento(file, avvisa) {
  const totale = file.size;
  const fuori = new Uint8Array(totale);
  let scritti = 0;
  while (scritti < totale) {
    const fine = Math.min(scritti + PEZZO_LETTURA, totale);
    const pezzo = new Uint8Array(await file.slice(scritti, fine).arrayBuffer());
    fuori.set(pezzo, scritti);
    scritti = fine;
    if (avvisa) avvisa(scritti / totale);
  }
  return fuori.buffer;
}

// `avanzamento` (facoltativo) riceve { fase, frazione }. `frazione` e' null
// quando non c'e' NIENTE di vero da misurare: la chiamata a crypto.subtle.encrypt
// e' una sola operazione atomica e non espone alcun progresso. In quel caso chi
// disegna deve mostrare un'animazione indeterminata, non una percentuale ferma.
export async function encryptFile(key, file, encryptName = false, avanzamento = null) {
  const buffer = await leggiFileIntero(file, (f) => {
    if (avanzamento) avanzamento({ fase: 'cifratura', frazione: f });
  });
  if (avanzamento) avanzamento({ fase: 'cifratura', frazione: null });
  const { ciphertext, iv } = await encryptBuffer(key, buffer);
  // Always store plain filename — content is encrypted, name is visible for usability
  // encryptName param kept for API compatibility but ignored (always plain)
  const encryptedName = 'plain:' + btoa(unescape(encodeURIComponent(file.name)));
  return { blob: new Blob([ciphertext], { type: 'application/octet-stream' }), iv, encryptedName, size: buffer.byteLength };
}

// Map common extensions to MIME types
function extToMime(filename) {
  const ext = (filename.split('.').pop() || '').toLowerCase();
  const map = {
    // Images
    jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png',
    gif: 'image/gif', webp: 'image/webp', svg: 'image/svg+xml',
    // Video
    mp4: 'video/mp4', mov: 'video/quicktime', avi: 'video/x-msvideo',
    mkv: 'video/x-matroska', webm: 'video/webm',
    // Audio
    mp3: 'audio/mpeg', wav: 'audio/wav', flac: 'audio/flac',
    ogg: 'audio/ogg', m4a: 'audio/mp4',
    // Documents
    pdf: 'application/pdf',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xls: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ppt: 'application/vnd.ms-powerpoint',
    pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    // Text / Code
    txt: 'text/plain', csv: 'text/csv', html: 'text/html',
    css: 'text/css', js: 'text/javascript', ts: 'text/typescript',
    json: 'application/json', xml: 'application/xml',
    // Archives
    zip: 'application/zip', tar: 'application/x-tar',
    gz: 'application/gzip', rar: 'application/x-rar-compressed',
    '7z': 'application/x-7z-compressed',
  };
  return map[ext] || 'application/octet-stream';
}

export async function decryptFile(key, encryptedBlob, ivBase64, encryptedNameStr) {
  const encBuffer   = await encryptedBlob.arrayBuffer();
  const plainBuffer = await decryptBuffer(key, encBuffer, ivBase64);
  let filename = 'decrypted_file';
  if (encryptedNameStr) {
    if (encryptedNameStr.startsWith('plain:')) {
      // Plain filename (standard since v2.1) — just decode base64
      try { filename = decodeURIComponent(escape(atob(encryptedNameStr.slice(6)))); } catch { filename = 'file'; }
    } else if (encryptedNameStr.includes(':')) {
      // Legacy fully-encrypted filename — decrypt with AES
      try {
        const [nameIvB64, nameCipherB64] = encryptedNameStr.split(':');
        const nameBuffer = await decryptBuffer(key, base64ToBuffer(nameCipherB64), nameIvB64);
        filename = dec.decode(nameBuffer);
      } catch { filename = 'decrypted_file'; }
    } else {
      filename = encryptedNameStr; // fallback
    }
  }
  // Use correct MIME type so browser opens/downloads the file properly
  const mimeType = extToMime(filename);
  return { plainBlob: new Blob([plainBuffer], { type: mimeType }), filename };
}

export function bufferToBase64(buffer) {
  const bytes = buffer instanceof ArrayBuffer ? new Uint8Array(buffer) : buffer;
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}

export function base64ToBuffer(base64) {
  const binary = atob(base64);
  const bytes  = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

export function formatBytes(bytes) {
  const lingua = typeof document !== 'undefined' ? document.documentElement.lang : '';
  if (!bytes || bytes === 0) return '0 ' + unitaByte(lingua, 'B');
  const k = 1024, sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const testo = String(parseFloat((bytes / Math.pow(k, i)).toFixed(1)));
  // 2026-09-16: in spagnolo la virgola decimale; 2026-09-17: francese, virgola e Ko/Mo/Go/To.
  return ((lingua === 'es' || lingua === 'fr' || lingua === 'de') ? testo.replace('.', ',') : testo) + ' ' + unitaByte(lingua, sizes[i]);
}

export function cryptoAvailable() {
  return typeof window !== 'undefined' && (
    window.isSecureContext ||
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1'
  );
}

// ── RECOVERY TOKEN HELPERS ────────────────────────────────

/**
 * Generate a cryptographically strong recovery token (32 bytes = 256 bit)
 * Returns base64url string — user must save this
 */
export function generateRecoveryToken() {
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  // Convert to readable format: groups of 6 chars separated by dashes
  const hex = Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
  return hex.match(/.{1,8}/g).join('-').toUpperCase(); // e.g. "A3F9BC2E-D84F1C7A-..."
}

/**
 * Derive a key from the recovery token using PBKDF2
 * Used to encrypt/decrypt the AES key backup
 */
export async function deriveRecoveryKey(recoveryToken, salt) {
  const cleanToken = recoveryToken.replace(/-/g, '').toUpperCase();
  const keyMaterial = await crypto.subtle.importKey(
    'raw', enc.encode(cleanToken), 'PBKDF2', false, ['deriveKey']
  );
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: base64ToBuffer(salt), iterations: 100000, hash: 'SHA-256' },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false, ['encrypt', 'decrypt']
  );
}

/**
 * Encrypt the AES key (exportable form) with the recovery key
 * Returns base64 string to store server-side
 */
export async function encryptKeyWithRecovery(aesKey, recoveryKey) {
  // Export the AES key as raw bytes
  const rawKey = await crypto.subtle.exportKey('raw', aesKey);
  // encryptBuffer already returns iv base64-encoded — don't re-encode it
  const { ciphertext, iv } = await encryptBuffer(recoveryKey, rawKey);
  return iv + ':' + bufferToBase64(ciphertext);
}

/**
 * Decrypt the AES key backup using the recovery key
 */
export async function decryptKeyFromRecovery(encryptedBackup, recoveryKey) {
  const [ivB64, cipherB64] = encryptedBackup.split(':');
  const rawKey = await decryptBuffer(recoveryKey, base64ToBuffer(cipherB64), ivB64);
  return crypto.subtle.importKey('raw', rawKey, { name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
}

/**
 * Hash of recovery token to send to server (server verifies this, never sees the token)
 */
export async function hashRecoveryToken(recoveryToken) {
  const cleanToken = recoveryToken.replace(/-/g, '').toUpperCase();
  const hash = await crypto.subtle.digest('SHA-256', enc.encode(cleanToken));
  return bufferToBase64(hash);
}

// ── SHARE LINK HELPERS ────────────────────────────────────
// Shared files/folders are re-encrypted with a fresh, single-purpose key so a
// link never exposes the account's master key. With no password that key
// travels only in the URL fragment (never sent to the server); with a
// password it's wrapped with a PBKDF2-derived key and stored server-side —
// the server holds ciphertext it cannot open either way.

export async function generateShareKey() {
  return crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt']);
}

export async function exportKeyRaw(key) {
  return bufferToBase64(await crypto.subtle.exportKey('raw', key));
}

export async function importKeyRaw(base64Key) {
  return crypto.subtle.importKey('raw', base64ToBuffer(base64Key), { name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt']);
}

/** Encrypt `keyToWrap` (a CryptoKey) with `wrappingKey` — returns "ivB64:cipherB64" */
export async function wrapKey(keyToWrap, wrappingKey) {
  const raw = await crypto.subtle.exportKey('raw', keyToWrap);
  // encryptBuffer already returns iv base64-encoded — don't re-encode it
  const { ciphertext, iv } = await encryptBuffer(wrappingKey, raw);
  return iv + ':' + bufferToBase64(ciphertext);
}

/** Reverse of wrapKey — returns a usable AES-GCM CryptoKey */
export async function unwrapKey(wrapped, wrappingKey) {
  const [ivB64, cipherB64] = wrapped.split(':');
  const raw = await decryptBuffer(wrappingKey, base64ToBuffer(cipherB64), ivB64);
  return crypto.subtle.importKey('raw', raw, { name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt']);
}

/** Re-encrypt an already-decrypted file buffer for a share, with a plain (readable) name */
export async function encryptForShare(shareKey, plainBuffer, filename) {
  const { ciphertext, iv } = await encryptBuffer(shareKey, plainBuffer);
  const encryptedName = 'plain:' + btoa(unescape(encodeURIComponent(filename)));
  return { blob: new Blob([ciphertext], { type: 'application/octet-stream' }), iv, encryptedName };
}

// URL-fragment-safe base64 (no +/= that some clients mangle when a link is
// copy-pasted or auto-linkified) — used only for the share key in the URL.
export function toUrlSafeBase64(b64) {
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
export function fromUrlSafeBase64(b64url) {
  let b64 = b64url.replace(/-/g, '+').replace(/_/g, '/');
  while (b64.length % 4) b64 += '=';
  return b64;
}

/** Decode a share item's name — same "plain:<b64>" convention as vault filenames */
export function decodeShareName(encryptedNameStr) {
  if (!encryptedNameStr) return 'file';
  if (encryptedNameStr.startsWith('plain:')) {
    try { return decodeURIComponent(escape(atob(encryptedNameStr.slice(6)))); } catch { return 'file'; }
  }
  return encryptedNameStr;
}
